import type { FieldErrors, LeadFormState } from '../types/lead.js';
import { errors as E, form } from '../config/collectionsLanding.js';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Israeli mobile-friendly: allow digits, spaces, dashes, parentheses, leading +.
export const MOBILE_RE = /^\+?[0-9][0-9\s\-()]{6,19}$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export function isValidMobile(value: string): boolean {
  const cleaned = value.trim();
  if (!MOBILE_RE.test(cleaned)) return false;
  const digits = cleaned.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

function platformNeedsOther(state: LeadFormState): boolean {
  const opt = form.invoicingPlatform.options.find((o) => o.value === state.invoicing_platform);
  return Boolean(opt?.freeText);
}

function paymentNeedsOther(state: LeadFormState): boolean {
  const opt = form.paymentMethods.options.find((o) => o.value === 'other');
  return Boolean(opt?.freeText) && state.payment_methods.includes('other');
}

/** Validate step 1: optional current systems. */
export function validateStep1(state: LeadFormState): FieldErrors {
  void state;
  return {};
}

/** Removed questionnaire step kept as a no-op for compatibility with older imports/tests. */
export function validateStep2(state: LeadFormState): FieldErrors {
  const errs: FieldErrors = {};
  if (state.biggest_pain_text.length > 500) {
    errs.biggest_pain_text = E.tooLong;
  }
  return errs;
}

/** Validate step 2: launch cost + contact. */
export function validateStep3(state: LeadFormState): FieldErrors {
  const errs: FieldErrors = {};

  const hasEmail = state.email.trim().length > 0;
  const hasMobile = state.mobile.trim().length > 0;
  if (!hasEmail) {
    errs.email = E.contactRequired;
  } else if (!isValidEmail(state.email)) {
    errs.email = E.emailInvalid;
  }
  if (!hasMobile) {
    errs.mobile = E.contactRequired;
  } else if (!isValidMobile(state.mobile)) {
    errs.mobile = E.mobileInvalid;
  }
  return errs;
}

export function validateStep(step: number, state: LeadFormState): FieldErrors {
  if (step === 0) return validateStep1(state);
  return validateStep3(state);
}

export function hasErrors(errs: FieldErrors): boolean {
  return Object.keys(errs).length > 0;
}

export const _internal = { platformNeedsOther, paymentNeedsOther };
